import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useChatNotesSync } from "./useLeadFormConfig";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import {
  assignLeadEndpoint,
  branchTransferEndpoint,
  bulkArchiveLeadsEndpoint,
  bulkFieldUpdateEndpoint,
  bulkStageUpdateEndpoint,
  leadsEndpoints,
  moveAssignmentEndpoint,
  unassignAssignmentEndpoint,
  peerTransferEndpoint,
} from "@/config/endpoints";
import { refetchCurrentLead } from "./useCurrentLead";
import { refetchChilds } from "./useChilds";
import {
  bulkOutcomeSubtitle,
  showBulkToast,
  showCompletedBulkPill,
} from "@/features/leads/components/bulk-update/BulkOperationPill";
import {
  invalidateLeadCaches,
  invalidateLeadBoard,
  invalidateLeadDetail,
  invalidateLeadTimeline,
  invalidateLeadAssignment,
  invalidateRecentActivityFeed,
} from "@/lib/queryClient";
import { PHONE_CHECK_KEY } from "./useCheckLeadPhone";

/**
 * Synchronously rewrite cached `branchId` on every lead row that just got
 * transferred. The Assign Lead modal computes per-user eligibility from this
 * cache (see `useAssignEligibility`), so without this patch the user sees
 * pre-transfer numbers ("29 of 255 leads", etc.) until the background
 * refetch lands — which can be a noticeable lag, and worse, can briefly
 * disable the Assign button on a lead the new branch users *can* receive.
 *
 * We touch every shape `extractLeadsFromLeadQueriesCache` knows about:
 *   - Infinite queries: `{ pages: [{ leads | data | stageLeads }] }`
 *   - Flat queries:    `{ leads: [...] }`
 *   - Board queries:   `BoardColumn[]` with each column's `.leads`
 * The matching `branch` relation is cleared (set to null) so a stale joined
 * object can't claim the wrong name in the UI; the next refetch refills it.
 */
/**
 * Apply `mapLead` to every lead row across every cached board/list shape
 * under the `["leads", ...]` prefix — the infinite-query (`pages[].leads` /
 * `.data` / `.stageLeads`), flat (`{ leads }`), and board (`BoardColumn[]`)
 * shapes. This is the shared walker behind every optimistic board-cache
 * patch; the cache stores RAW leads (adaptLead runs in each query's `select`
 * on read), so patches mutate raw fields and the card re-derives on render.
 */
export function mapLeadsInBoardCache(
  queryClient: QueryClient,
  mapLead: (lead: any) => any,
) {
  queryClient.setQueriesData<any>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) && query.queryKey[0] === "leads",
    },
    (data: any) => {
      if (!data) return data;

      // useInfiniteQuery shape
      if (Array.isArray(data.pages)) {
        return {
          ...data,
          pages: data.pages.map((page: any) => {
            if (!page) return page;
            if (Array.isArray(page.leads)) {
              return { ...page, leads: page.leads.map(mapLead) };
            }
            if (Array.isArray(page.data)) {
              return { ...page, data: page.data.map(mapLead) };
            }
            if (Array.isArray(page.stageLeads)) {
              return { ...page, stageLeads: page.stageLeads.map(mapLead) };
            }
            return page;
          }),
        };
      }

      // Flat { leads: [...] }
      if (Array.isArray(data.leads)) {
        return { ...data, leads: data.leads.map(mapLead) };
      }

      // Board: BoardColumn[] with .leads
      if (Array.isArray(data)) {
        return data.map((col: any) =>
          col && Array.isArray(col.leads)
            ? { ...col, leads: col.leads.map(mapLead) }
            : col,
        );
      }

      return data;
    },
  );
}

/**
 * Lead scalars that a drawer field edit can change AND that the card renders
 * directly off the raw row — safe to write into the board/list cache
 * optimistically (see `updateLead.onMutate`). `priority` drives the card's
 * ring colour; the rest are the card's contact line.
 *
 * Relation-backed fields (`leadSourceId`, `campaignId`, stage / status /
 * assignment) are NOT here: the card reads their NAME off a joined object, so
 * a scalar-only patch would render a stale label. Those keep waiting for the
 * refetch.
 */
const CARD_PATCHABLE_LEAD_FIELDS = [
  "priority",
  "name",
  "phone",
  "phoneCountryCode",
  "email",
  "whatsapp",
  "location",
  "gender",
] as const;

function patchLeadBranchIdInCache(
  queryClient: QueryClient,
  leadIds: (string | number)[],
  newBranchId: number,
) {
  const idSet = new Set(leadIds.map((id) => Number(id)));
  if (idSet.size === 0) return;
  mapLeadsInBoardCache(queryClient, (lead) =>
    lead && idSet.has(Number(lead.id))
      ? { ...lead, branchId: newBranchId, branch: null }
      : lead,
  );
}

/**
 * Normalise a drawer note into the SERVER's note shape before it goes into a
 * card cache.
 *
 * The two shapes are NOT the same and that is the whole bug: the server's
 * `Notes` row (and so every list payload) carries the text in `content`, while
 * the drawer's form state carries it in `note` (`NoteSection` → `useLeadNotes`
 * / `useChildNotes` build `{ note, createdById, createdAt, createdByName }`).
 * Prepending that raw object left the card reading `notes[0].content ===
 * undefined`, so `pickLatestNote` returned `{ content: "" }` and the card fell
 * back to its "Add a note..." placeholder — and because the fake note carries a
 * fresh `createdAt` it also WON the latest-note race, blanking whatever note
 * the card was already showing. Notes attached to a follow-up never hit this
 * path (they take the `invalidateLeadBoard()` branch and come back from the
 * server correctly shaped), which is exactly why those appeared and plain notes
 * did not.
 *
 * Returns `null` when there is no text to show, so the caller can fall back to
 * a refetch instead of poisoning the card with an empty latest note.
 */
export function toCardNote(note: any): Record<string, any> | null {
  const text =
    typeof note?.content === "string" && note.content.trim()
      ? note.content
      : typeof note?.note === "string"
        ? note.note
        : "";
  if (!text.trim()) return null;
  return {
    ...note,
    content: text,
    createdAt: note?.createdAt ?? new Date().toISOString(),
  };
}

/**
 * Optimistic note patch: prepend `note` to the matching lead's raw `notes`
 * array in the board caches so the card's latest-note preview
 * (`pickLatestNote` → `notes[0].content`) updates INSTANTLY without a board
 * refetch (the board `["leads"]` query is the single heaviest DB call). The
 * note content is authoritative (it's exactly what the user typed), and the
 * row stays in place — so this is correct, not a guess. If the lead isn't in
 * any board cache it's not on screen, so the no-op is harmless.
 *
 * Returns false when the note carried no text — the caller must then refetch
 * rather than leave the card silently unchanged.
 */
function prependNoteToLeadInBoardCache(
  queryClient: QueryClient,
  leadId: string | number,
  note: any,
): boolean {
  const id = Number(leadId);
  if (!Number.isFinite(id)) return false;
  const cardNote = toCardNote(note);
  if (!cardNote) return false;
  mapLeadsInBoardCache(queryClient, (lead) => {
    if (!lead || Number(lead.id) !== id) return lead;
    const prev = Array.isArray(lead.notes) ? lead.notes : [];
    return { ...lead, notes: [cardNote, ...prev] };
  });
  return true;
}

/**
 * Optimistic STATUS-board re-bucket: move a lead's card to its new status
 * column in the `["leads","board"]` cache so the acting user sees a
 * drawer-initiated stage/status change INSTANTLY. The board behind the drawer
 * otherwise rides the server's 12s board-cache throttle, so a refetch returns
 * the old column for up to 12s. This patch paints the correct column in that
 * gap; it is ADDITIVE — the mutation still refetches and reconciles to server
 * truth. The board renders from local state re-seeded off this query's
 * `select`, so a cache patch propagates to a mounted board with no component
 * changes.
 *
 * Correctness guardrails (a wrong column must NEVER ship — fall back to the
 * refetch when uncertain):
 *   - ONLY the status board (`["leads","board"]`), whose columns key on the
 *     lead's OWN statusId — unambiguous. The stage board, the flat list, and
 *     all child/deal moves are intentionally NOT patched (ambiguous placement
 *     via routeChild / cross-stage / non-grouped); they keep the refetch.
 *   - Names come from the DESTINATION column (authoritative), never a guess.
 *   - Atomic per cache entry: if the destination status isn't a visible column
 *     here, or the lead isn't on this board/page, skip THAT entry entirely (no
 *     orphan removal) and let the refetch reconcile.
 *   - Called from `onSuccess` ONLY, so a server-rejected move never paints.
 */
export function optimisticStatusMoveInBoardCache(
  queryClient: QueryClient,
  opts: { leadId: string | number; toStatusId: number; toSubStatusId?: number | null },
) {
  const id = Number(opts.leadId);
  const toStatus = Number(opts.toStatusId);
  if (!Number.isFinite(id) || !Number.isFinite(toStatus)) return;

  queryClient.setQueriesData<any>(
    {
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        q.queryKey[0] === "leads" &&
        q.queryKey[1] === "board",
    },
    (data: any) => {
      // Status-board cache is `BoardColumn[]`. Anything else (shouldn't match
      // the predicate) is left untouched.
      if (!Array.isArray(data)) return data;

      // Locate the lead's current (source) column.
      let fromIdx = -1;
      let movedLead: any = null;
      for (let i = 0; i < data.length; i++) {
        const leads = data[i]?.leads;
        if (!Array.isArray(leads)) continue;
        const found = leads.find((l: any) => Number(l?.id) === id);
        if (found) {
          fromIdx = i;
          movedLead = found;
          break;
        }
      }
      // Not on this board / not on a loaded page → no-op (refetch reconciles).
      if (!movedLead) return data;

      const destIdx = data.findIndex(
        (col: any) => Number(col?.statusId) === toStatus,
      );
      // GUARDRAIL: destination status is not a visible column here → skip this
      // entry entirely so we never invent a column / orphan the row.
      if (destIdx === -1) return data;

      const destCol = data[destIdx];
      const sub =
        opts.toSubStatusId != null
          ? destCol?.subStatuses?.find(
              (s: any) => Number(s?.id) === Number(opts.toSubStatusId),
            ) ?? null
          : null;

      // Patch RAW fields so the query's `select` (adaptLead) re-derives the
      // card's status/stage/sub-status labels from the destination column.
      const patched = {
        ...movedLead,
        statusId: toStatus,
        status: {
          ...(movedLead.status ?? {}),
          id: toStatus,
          name: destCol.statusName,
        },
        stageId: destCol.stageId,
        stage: {
          ...(movedLead.stage ?? {}),
          id: destCol.stageId,
          name: destCol.stageName,
        },
        subStatusId: sub?.id ?? null,
        subStatus: sub ? { id: sub.id, name: sub.name } : null,
      };

      return data.map((col: any, i: number) => {
        if (i === fromIdx && i === destIdx) {
          // Same column (sub-status-only change) — replace in place, no move.
          return {
            ...col,
            leads: col.leads.map((l: any) =>
              Number(l?.id) === id ? patched : l,
            ),
          };
        }
        if (i === fromIdx) {
          return {
            ...col,
            leads: col.leads.filter((l: any) => Number(l?.id) !== id),
            totalCount: Math.max(0, (col.totalCount ?? col.leads.length) - 1),
          };
        }
        if (i === destIdx) {
          return {
            ...col,
            leads: [patched, ...col.leads],
            totalCount: (col.totalCount ?? col.leads.length) + 1,
          };
        }
        return col;
      });
    },
  );
}
import {
  invalidateFollowUpDashboardQueries,
  isFollowUpPatchType,
} from "@/features/Dashboard/api/invalidateFollowUpDashboardQueries";

export function useLeadMutations() {
  const queryClient = useQueryClient();
  // "Chat & notes sync": a note (or a follow-up / deadline note) saved here is
  // posted into the chat by the server. Mark cached threads stale — free, no
  // request — so a chat opened next shows it; open chats get the live push.
  const chatNotesSync = useChatNotesSync();
  const markChatStaleForNoteSync = () => {
    if (!chatNotesSync) return;
    queryClient.invalidateQueries({
      queryKey: ["internal-chat"],
      refetchType: "none",
    });
  };

  // 1. CREATE LEAD
  const createLead = useMutation({
    mutationFn: async (data: any) => {
      // Post to your backend (e.g., /api/leads)
      return axiosInstance.post(leadsEndpoints, data);
    },
    onSuccess: (res: any) => {
      // "Lead user / Deal user": a deal-user creator is left off a lead with
      // no deal (it's created unassigned) — the server says so.
      const holderNotice = res?.data?.lead?.holderNotice;
      if (holderNotice) toast.warning(holderNotice, { duration: 10000 });
      else toast.success("Lead created successfully");
      // A new lead only affects the list / board views + header counts.
      invalidateLeadBoard();
      // `lead_created` shows in the dashboard feed (no socket refresh any more).
      invalidateRecentActivityFeed();
      // The number just became taken — drop any cached "available" verdict so
      // the next Add Lead form doesn't read a stale one.
      queryClient.invalidateQueries({ queryKey: [PHONE_CHECK_KEY] });
    },
    onError: (error: any) => {
      console.error(error);
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;
      const msg =
        status === 409
          ? serverMsg ||
            "A lead with this phone number already exists for this tenant"
          : serverMsg || "Failed to create lead";
      toast.error(msg);
    },
  });

  // 2. UPDATE LEAD
  const updateLead = useMutation({
    mutationFn: async ({ id, data }: { id: any; data: any }) => {
      const { branchId: _branchId, ...payload } = data;
      return axiosInstance.patch(`${leadsEndpoints}/${id}`, payload);
    },
    onMutate: async ({ id, data }) => {
      const { branchId: _b, ...patch } = data;
      await queryClient.cancelQueries({ queryKey: ["get-leads", id] });
      // PREFIX-matched, not exact. `useCurrentLead` appends a third key element
      // (the anchor/cf params) whenever a Verified filter is active, and React
      // Query v5 resolves getQueryData/setQueryData by EXACT key hash — so the
      // 2-element form silently patched nothing precisely under the anchor
      // view, losing the optimistic update and its rollback there.
      const previousEntries = queryClient.getQueriesData<any>({
        queryKey: ["get-leads", id],
      });
      const previous = previousEntries[0]?.[1];
      queryClient.setQueriesData({ queryKey: ["get-leads", id] }, (old: any) => {
        if (!old) return old;
        const next = { ...old };
        if (patch.name !== undefined) next.name = patch.name;
        if (patch.phone !== undefined) next.phone = patch.phone;
        if (patch.phoneCountryCode !== undefined)
          next.phoneCountryCode = patch.phoneCountryCode;
        if (patch.location !== undefined) next.location = patch.location;
        if (patch.email !== undefined) next.email = patch.email;
        if (patch.whatsapp !== undefined) next.whatsapp = patch.whatsapp;
        if (patch.gender !== undefined) next.gender = patch.gender;
        if (patch.priority !== undefined) next.priority = patch.priority;
        if (patch.leadSourceId !== undefined && patch.leadSourceId !== "") {
          const lsId = Number(patch.leadSourceId);
          next.leadSourceId = lsId;
          if (next.leadSource && typeof next.leadSource === "object") {
            next.leadSource = { ...next.leadSource, id: lsId };
          }
        }
        if (patch.campaignId !== undefined && patch.campaignId !== "") {
          const cId = Number(patch.campaignId);
          next.campaignId = cId;
          if (next.campaign && typeof next.campaign === "object") {
            next.campaign = { ...next.campaign, id: cId };
          }
        }
        return next;
      });
      // The CARD caches (`["leads", ...]` grid / list / board) are a separate
      // cache from the drawer's `["get-leads", id]` patched above, and the
      // `invalidateLeadBoard()` refetch in `onSuccess` can only be as fresh as
      // the server's Redis list-data entry. Patch the card row here too so the
      // edit lands on the card the user is looking at INSTANTLY, with no
      // round-trip — this is what makes the priority ring recolour in
      // realtime instead of after a refresh.
      //
      // Scalars ONLY. `leadSourceId` / `campaignId` are deliberately excluded:
      // the card renders the source/campaign NAME off the joined relation, so
      // patching just the id would leave the old name showing next to the new
      // id — worse than waiting for the refetch.
      const cardPatch: Record<string, any> = {};
      for (const field of CARD_PATCHABLE_LEAD_FIELDS) {
        if (patch[field] !== undefined) cardPatch[field] = patch[field];
      }
      // The drawer sends "High" / "Low"; the server lowercases it onto the
      // `Priorities` enum. Match that here so the optimistic row is identical
      // to the one the refetch brings back.
      if (typeof cardPatch.priority === "string") {
        cardPatch.priority = cardPatch.priority.toLowerCase();
      }
      let previousCard: Record<string, any> | undefined;
      if (Object.keys(cardPatch).length > 0) {
        mapLeadsInBoardCache(queryClient, (lead: any) => {
          if (!lead || Number(lead.id) !== Number(id)) return lead;
          // Snapshot the pre-edit values off the first row we hit so `onError`
          // can put them back (every cached shape holds the same raw row).
          if (!previousCard) {
            previousCard = {};
            for (const field of Object.keys(cardPatch)) {
              previousCard[field] = lead[field];
            }
          }
          return { ...lead, ...cardPatch };
        });
      }

      return { previous, previousCard };
    },
    onError: (_err, variables, context) => {
      if (context?.previous !== undefined) {
        // Prefix-matched for the same reason as the onMutate patch above.
        queryClient.setQueriesData(
          { queryKey: ["get-leads", variables.id] },
          context.previous
        );
      }
      if (context?.previousCard) {
        const revert = context.previousCard;
        mapLeadsInBoardCache(queryClient, (lead: any) =>
          lead && Number(lead.id) === Number(variables.id)
            ? { ...lead, ...revert }
            : lead,
        );
      }
      const status = (_err as any)?.response?.status;
      const serverMsg = (_err as any)?.response?.data?.message;
      const msg =
        status === 409
          ? serverMsg ||
            "A lead with this phone number already exists for this tenant"
          : serverMsg || "Failed to update lead";
      toast.error(msg);
    },
    onSuccess: (_data, variables) => {
      toast.success("Lead updated successfully");
      // `onMutate` already optimistically patched the open drawer
      // (`["get-leads", id]`). A field edit can change anything the card
      // renders — including lead-level custom-field values and tags, which a
      // fixed allowlist would silently miss — so always refresh the board.
      // `refetchType:"active"` makes this a no-op when the board isn't
      // mounted (e.g. editing from a non-board route), so it's cheap and
      // guarantees the user sees their own edit on the card they came from.
      invalidateLeadTimeline(variables?.id);
      refetchCurrentLead();
      refetchChilds();
      invalidateLeadBoard();
      // An edit may have freed or claimed a number — re-probe on next check.
      queryClient.invalidateQueries({ queryKey: [PHONE_CHECK_KEY] });
    },
  });

  // 3. DELETE LEAD (soft delete — moves to Trash under Settings → System Logs)
  const deleteLead = useMutation({
    mutationFn: async (id: number) => {
      return axiosInstance.delete(`${leadsEndpoints}/${id}`);
    },
    onSuccess: () => {
      toast.success("Lead moved to Trash");
      invalidateLeadBoard();
      queryClient.invalidateQueries({ queryKey: ["trashed-leads"] });
    },
    onError: (error: any) => {
      console.log(error, "error");
      toast.error("Failed to delete lead");
    },
  });

  const updateStage = useMutation({
    mutationFn: async ({ id, data }: any) => {
      return await axiosInstance.patch(
        `${leadsEndpoints}/stage-update/${id}`,
        data
      );
    },
    onSuccess: (_data, variables) => {
      toast.success("Stage Updated");
      // Optimistically re-bucket the card on the status board so the acting
      // user sees the move INSTANTLY — the board behind the drawer otherwise
      // rides the server's 12s board-cache throttle and would show the old
      // column for up to 12s. Guarded + additive: the refetch below still
      // reconciles to server truth, and the helper no-ops (falls back to that
      // refetch) for anything it can't place unambiguously (stage board, flat
      // list, dest status not visible, lead not loaded).
      if (variables?.data?.statusId != null) {
        optimisticStatusMoveInBoardCache(queryClient, {
          leadId: variables.id,
          toStatusId: Number(variables.data.statusId),
          toSubStatusId:
            variables.data.subStatusId != null
              ? Number(variables.data.subStatusId)
              : null,
        });
      }
      // A stage move changes which board column the lead sits in and writes
      // an activity row — refresh board + timeline + the open drawer (lead,
      // deals, current deal, hover, tracks). Our own socket echo no longer
      // refetches anything, so this is the actor's only refresh.
      invalidateLeadBoard();
      invalidateLeadTimeline(variables?.id);
      invalidateLeadDetail();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to Update Stage";
      toast.error(msg);
    },
  });

  const patchLeadSilently = useMutation({
    mutationFn: async ({
      id,
      data,
      type = "note",
    }: {
      id: string | number;
      data: any;
      type?: string;
    }) => {
      // We use PATCH to only update changed fields
      return await axiosInstance.patch(`${leadsEndpoints}/${id}`, {
        type,
        ...data,
      });
    },
    onSuccess: (_data, variables) => {
      invalidateLeadTimeline(variables?.id);
      refetchCurrentLead();
      markChatStaleForNoteSync();

      const note = variables?.data?.note;
      const isNote = !isFollowUpPatchType(variables?.type) && !!note;
      if (isNote) {
        // OPTIMISTIC: a note only changes the card's latest-note preview and
        // keeps the row in place — patch it into the board cache instead of
        // refetching the (expensive) board query. Notes are the highest-
        // frequency board mutation, so this is the biggest DB-load saving and
        // the card updates instantly (faster than a refetch).
        // Falls back to the refetch when the note has no renderable text, so
        // the card can never be left stale (or blanked) by a bad patch.
        if (!prependNoteToLeadInBoardCache(queryClient, variables.id, note)) {
          invalidateLeadBoard();
        }
      } else {
        // Follow-up / deadline / other: changes the follow-up badge (and
        // dashboard widgets) — keep the board refetch (active-only).
        invalidateLeadBoard();
        if (isFollowUpPatchType(variables?.type)) {
          invalidateFollowUpDashboardQueries(queryClient);
        }
      }
    },
    onError: (error: any) => {
      // IMPORTANT: Even if success is silent, failure should usually be loud
      // so the user knows their data wasn't saved.
      const msg = error.response?.data?.message || "Auto-save failed";
      toast.error(msg);
    },
  });

  // 1. CREATE LEAD
  const assignLeads = useMutation({
    mutationFn: async (data: {
      selectedLeadIds: (string | number)[];
      selectedUser: string | number;
      level?: "lead" | "child";
      confirmCrossRevoke?: boolean;
      /**
       * Leads whose DEAL-level same-role holders were listed in the confirm
       * dialog. Cross-level revocation applies to THESE leads only, so the
       * write can never exceed what the user was shown. Prefer this over the
       * blanket `confirmCrossRevoke` for any multi-lead assign.
       */
      crossRevokeLeadIds?: (string | number)[];
      archiveOldHistory?: boolean;
    }) => {
      const {
        selectedLeadIds,
        selectedUser,
        level = "lead",
        confirmCrossRevoke,
        crossRevokeLeadIds,
        archiveOldHistory,
      } = data;
      return axiosInstance.post(assignLeadEndpoint, {
        leads: selectedLeadIds,
        userId: selectedUser,
        level,
        confirmCrossRevoke: confirmCrossRevoke === true,
        crossRevokeLeadIds: Array.isArray(crossRevokeLeadIds)
          ? crossRevokeLeadIds.map((id) => Number(id))
          : [],
        archiveOldHistory: archiveOldHistory === true,
      });
    },
    // Paint the assignment the moment the user confirms. The confirm dialog
    // closes immediately, but the server write (policy stack, activity row,
    // follow-up reset) plus the list refetch take seconds — during which the
    // card still showed the old holder and stayed on the "Unassigned" list
    // (user, 2026-09-17: "the card is not refetching instantly"). The cache
    // holds RAW rows, so we add the new holder to `LeadUsers` (what
    // `adaptLead` reads for the avatar) and drop the lead from any list
    // scoped to unassigned leads. The success refetch below reconciles; a
    // server refusal re-syncs in onError.
    onMutate: (variables) => {
      const ids = new Set(
        (variables?.selectedLeadIds ?? []).map((id) => Number(id)),
      );
      if (ids.size === 0 || (variables.level ?? "lead") !== "lead") return;
      const userId = Number(variables.selectedUser);
      // The new holder's name/avatar, from whichever users list is cached.
      const cachedUser = queryClient
        .getQueriesData<any>({ queryKey: ["users"] })
        .flatMap(([, data]) => (Array.isArray(data) ? data : data?.users ?? []))
        .find((u: any) => Number(u?.id) === userId);

      mapLeadsInBoardCache(queryClient, (lead: any) => {
        if (!lead || !ids.has(Number(lead.id))) return lead;
        const holder = {
          isLeadOwner: true,
          isRevoked: false,
          user: cachedUser
            ? {
                id: cachedUser.id,
                name: cachedUser.name,
                avatar: cachedUser.avatar,
                role: cachedUser.role,
                isActive: cachedUser.isActive,
              }
            : { id: userId, name: "Assigning…" },
        };
        const others = (lead.LeadUsers ?? []).filter(
          (u: any) => Number(u?.user?.id) !== userId,
        );
        return {
          ...lead,
          // Legacy singular wins in adaptLead — clear it so the list below is read.
          assignedUser: null,
          LeadUsers: [holder, ...others],
        };
      });

      // A list filtered to unassigned leads no longer contains these rows.
      queryClient.setQueriesData<any>(
        {
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === "leads" &&
            (q.queryKey[2] as any)?.assignmentStatus === "unassigned",
        },
        (data: any) => {
          if (!data || !Array.isArray(data.pages)) return data;
          const keep = (l: any) => !ids.has(Number(l?.id));
          return {
            ...data,
            pages: data.pages.map((page: any) =>
              page && Array.isArray(page.leads)
                ? { ...page, leads: page.leads.filter(keep) }
                : page,
            ),
          };
        },
      );
    },
    onSuccess: (
      response: {
        data?: { mode?: string; jobId?: string; totalLeads?: number };
      },
      variables,
    ) => {
      if (response?.data?.mode === "async") {
        // Above the server threshold the assignment runs chunked on the
        // worker — the progress pill polls it and refreshes caches when done.
        showBulkToast(response.data.jobId, {
          kind: "assign",
          total: Number(response.data.totalLeads ?? 0),
        });
        return;
      }
      // Sync branch — pop the same pill in its done state so every bulk
      // (large or small) reports through one consistent surface.
      const assignedCount = variables?.selectedLeadIds?.length ?? 1;
      if (assignedCount > 1) {
        showCompletedBulkPill({ kind: "assign", total: assignedCount });
      } else {
        toast.success("Lead assigned successfully");
      }
      // Assignment changes are visible across grid, board, infinite list,
      // open lead drawer, and the assign-eligibility hook (driven by
      // roles-pipeline). `invalidateLeadAssignment` busts exactly those
      // (roles-pipeline + board + detail) without the timeline / follow-up
      // / automation prefixes a plain assign doesn't touch. It already
      // busts the drawer (get-leads / childs / get-child), so no extra
      // refetchCurrentLead/refetchChilds (those fired each GET twice).
      // Assignment writes an activity row + may reset the follow-up.
      invalidateLeadAssignment();
      invalidateLeadTimeline();
    },
    onError: (error: { response?: { status?: number; data?: { message?: string } } }) => {
      // Undo the optimistic paint — the refetch restores what the server holds.
      invalidateLeadBoard();
      const status = error?.response?.status;
      const msg = error?.response?.data?.message;
      if (status === 409) {
        toast.error(
          msg ||
            "This role is on an application for one or more selected leads. Open each lead to assign or confirm from the drawer.",
        );
        return;
      }
      toast.error(msg || "Failed to assign lead");
    },
  });

  // Move an existing assignee between lead-level and child-level (or
  // between two children of the same lead). The server atomically revokes
  // the source row and reactivates the user at the destination, running
  // the full assign policy stack on the destination side. A 4xx maps to a
  // toast describing the underlying constraint (e.g. assignee no longer
  // active on source, or branch / CF / scope policy refused the move).
  const moveAssignment = useMutation({
    mutationFn: async (data: {
      leadId: number;
      userId: number;
      fromChildId?: number | null;
      toChildId?: number | null;
    }) => {
      return axiosInstance.post(moveAssignmentEndpoint, {
        leadId: data.leadId,
        userId: data.userId,
        fromChildId: data.fromChildId ?? null,
        toChildId: data.toChildId ?? null,
      });
    },
    onSuccess: () => {
      toast.success("Assignee moved");
      // invalidateLeadAssignment covers board + drawer; timeline for the
      // activity row the move writes.
      invalidateLeadAssignment();
      invalidateLeadTimeline();
    },
    onError: (error: { response?: { status?: number; data?: { message?: string } } }) => {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message;
      if (status === 404) {
        toast.error(msg || "Assignment no longer active — refresh and retry");
        return;
      }
      toast.error(msg || "Failed to move assignee");
    },
  });

  /**
   * "Unassign" from the drawer's Assigned-to popover — revokes ONE user's
   * lead-level or deal-level assignment. Server gate: admin, or ADMIN scope on
   * the stage the assignment lives in (`leadService.unassignUser`).
   */
  const unassignAssignee = useMutation({
    mutationFn: async (data: {
      leadId: number;
      userId: number;
      childId?: number | null;
      userName?: string;
    }) => {
      return axiosInstance.post(unassignAssignmentEndpoint, {
        leadId: data.leadId,
        userId: data.userId,
        childId: data.childId ?? null,
      });
    },
    onSuccess: (_res, vars) => {
      toast.success(
        vars.userName ? `${vars.userName} unassigned` : "User unassigned",
      );
      invalidateLeadAssignment();
      invalidateLeadTimeline(vars.leadId);
      // "Previously assigned" in the popover now lists the removed user.
      queryClient.invalidateQueries({ queryKey: ["lead-revoke-summary"] });
    },
    onError: (error: { response?: { status?: number; data?: { message?: string } } }) => {
      const msg = error?.response?.data?.message;
      if (error?.response?.status === 404) {
        toast.error(msg || "Assignment no longer active — refresh and retry");
        return;
      }
      toast.error(msg || "Failed to unassign user");
    },
  });

  /**
   * "Transfer to peers" — hand a lead (lead-level) or deal (child-level) the
   * CURRENT user holds to a same-role peer. The server verifies the actor holds
   * the slot, the recipient shares the role, runs the full branch/CF policy
   * stack, and revokes the actor (handover). The actor LOSES the lead, so we
   * invalidate the board (it leaves their list) on top of the usual refetches.
   */
  const peerTransfer = useMutation({
    mutationFn: async (data: {
      leadId: number;
      toUserId: number;
      childId?: number | null;
    }) => {
      return axiosInstance.post(peerTransferEndpoint, {
        leadId: data.leadId,
        toUserId: data.toUserId,
        childId: data.childId ?? null,
      });
    },
    onSuccess: () => {
      toast.success("Transferred to peer");
      // The actor handed the lead off and lost access — invalidate the board
      // so it drops from their list. Deliberately NOT refetching the current
      // lead/children: the actor can no longer read them, so a refetch would
      // 403. The peer sheet closes the detail drawer on success instead.
      // (invalidateLeadAssignment would refetch that drawer and double the
      // board bust — board only.)
      invalidateLeadBoard();
      invalidateRecentActivityFeed(); // `peer_transferred` feed row
    },
    onError: (error: {
      response?: { status?: number; data?: { message?: string } };
    }) => {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message;
      if (status === 403) {
        toast.error(msg || "You can't transfer this lead");
        return;
      }
      toast.error(msg || "Failed to transfer");
    },
  });

  /**
   * Branch transfer for N leads.
   *
   * Server response shape (mirrors bulkStageUpdate / bulkArchive):
   * - sync  (HTTP 200): `{ status, mode: "sync", count }`
   * - async (HTTP 202): `{ status, mode: "async", jobId, totalLeads }`
   *
   * In the async branch (above `BULK_BRANCH_TRANSFER_SYNC_THRESHOLD`,
   * default 200 on the server) we cannot patch the cached `branchId`
   * because the worker hasn't run yet — caches stay as-is and the user
   * sees the new branch after the next refetch / page nav. The caller can
   * optionally poll `useBulkJobStatus(jobId)` to drive a progress toast.
   */
  const branchTransfer = useMutation({
    mutationFn: async (data: any) => {
      const { selectedLeadIds, selectedBranch, archiveOldHistory } = data;
      const res = await axiosInstance.post(branchTransferEndpoint, {
        leads: selectedLeadIds,
        branchId: selectedBranch,
        archiveOldHistory: archiveOldHistory === true,
      });
      return res.data;
    },
    onSuccess: (data: any, variables: any) => {
      if (data?.mode === "async") {
        // Progress pill polls the job and refreshes caches on completion.
        // Don't patch caches here — the worker hasn't done the work yet.
        showBulkToast(data.jobId, {
          kind: "branch-transfer",
          total: Number(data.totalLeads ?? 0),
        });
        return;
      }

      // Sync branch — same pill surface as the async path, popped done.
      const transferredCount = Array.isArray(variables?.selectedLeadIds)
        ? variables.selectedLeadIds.length
        : 0;
      showCompletedBulkPill({
        kind: "branch-transfer",
        total: transferredCount,
      });

      // Synchronously reflect the new branchId on the cached lead rows so
      // the Assign Lead modal's eligibility math (useAssignEligibility,
      // which reads from this cache) updates on the very next render —
      // before the background refetch returns. Without this, opening the
      // Assign panel right after a transfer shows pre-transfer counts
      // (e.g. "29 of 255 leads") for several seconds.
      const newBranchId = Number(variables?.selectedBranch);
      const ids: (string | number)[] = Array.isArray(variables?.selectedLeadIds)
        ? variables.selectedLeadIds
        : [];
      if (Number.isFinite(newBranchId) && ids.length > 0) {
        patchLeadBranchIdInCache(queryClient, ids, newBranchId);
      }

      // Branch transfer invalidates everything that scopes off branch:
      // invalidateLeadCaches busts list/board, the open drawer (get-leads),
      // deals (childs / get-child) and the count/stats tiles. No extra
      // refetchCurrentLead/refetchChilds — they duplicated those GETs.
      // useAssignEligibility is derived from these caches.
      invalidateLeadCaches();
      invalidateLeadTimeline();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to transfer branch";
      toast.error(msg);
    },
  });

  /**
   * Bulk stage / status / sub-status update.
   *
   * Server response shape:
   * - sync (HTTP 200): `{ status, mode: "sync", success, skipped, failed,
   *   totalRequested, resolvedTarget }`
   * - async (HTTP 202): `{ status, mode: "async", jobId, totalLeads }`
   *
   * Caller decides what to do with the async branch (typically poll
   * `useBulkJobStatus(jobId)`).
   */
  const bulkStageUpdate = useMutation({
    mutationFn: async (data: {
      leads: (string | number)[];
      stageId?: string | number;
      statusId?: string | number;
      subStatusId?: string | number;
      /** Precise deal targeting — only leads with a deal currently matching
       *  the source (stages, optionally narrowed by status / sub-status) are
       *  moved. Empty/absent = plain lead-level bulk move. */
      sourceStageIds?: (string | number)[];
      sourceStatusIds?: (string | number)[];
      sourceSubStatusIds?: (string | number)[];
      /** Precise mode only — move JUST the matching deal(s); lead row stays. */
      dealOnly?: boolean;
      /** Admin-scoped: skip the target stage's requirement checklist. */
      force?: boolean;
    }) => {
      const cleanIds = (v?: (string | number)[]) =>
        (v ?? [])
          .map((id) => Number(id))
          .filter((id) => !isNaN(id) && id > 0);
      const sourceStageIds = cleanIds(data.sourceStageIds);
      const sourceStatusIds = cleanIds(data.sourceStatusIds);
      const sourceSubStatusIds = cleanIds(data.sourceSubStatusIds);
      const precise =
        sourceStageIds.length > 0 ||
        sourceStatusIds.length > 0 ||
        sourceSubStatusIds.length > 0;
      const payload = {
        leads: data.leads.map((id) => Number(id)).filter((id) => !isNaN(id)),
        stageId:
          data.stageId !== undefined && data.stageId !== ""
            ? Number(data.stageId)
            : undefined,
        statusId:
          data.statusId !== undefined && data.statusId !== ""
            ? Number(data.statusId)
            : undefined,
        subStatusId:
          data.subStatusId !== undefined && data.subStatusId !== ""
            ? Number(data.subStatusId)
            : undefined,
        ...(sourceStageIds.length > 0 ? { sourceStageIds } : {}),
        ...(sourceStatusIds.length > 0 ? { sourceStatusIds } : {}),
        ...(sourceSubStatusIds.length > 0 ? { sourceSubStatusIds } : {}),
        ...(precise && data.dealOnly === true ? { dealOnly: true } : {}),
        ...(data.force === true ? { force: true } : {}),
      };
      const res = await axiosInstance.post(bulkStageUpdateEndpoint, payload);
      return res.data;
    },
    onSuccess: (data: any) => {
      if (data?.mode === "async") {
        // Progress pill polls the job and refreshes caches on completion.
        showBulkToast(data.jobId, {
          kind: "stage",
          total: Number(data.totalLeads ?? 0),
        });
        return;
      }
      const success = Number(data?.success?.length ?? 0);
      // Sync branch — same pill surface as the async path, popped done.
      showCompletedBulkPill(
        { kind: "stage", total: Number(data?.totalRequested ?? success) },
        {
          done: success,
          // Carries the server's own per-row `reason` (e.g. the requirement
          // that blocked the move, or "not in the selected source stage"),
          // so a partial bulk is actionable instead of a bare "8 failed".
          subtitle: bulkOutcomeSubtitle(data),
        },
      );
      // Bulk stage move: board columns + counts + activity timeline, and
      // deals (precise / deal-only moves change childs + the card hover).
      invalidateLeadBoard();
      invalidateLeadTimeline();
      invalidateLeadDetail();
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message || "Failed to bulk-update leads";
      toast.error(msg);
    },
  });

  /**
   * Bulk field update (priority / leadSourceId / campaignId).
   *
   * Always synchronous on the server (single `updateMany`), so the
   * response is always `{ status, mode: "sync", success, skipped, failed,
   * totalRequested, resolvedFields }`. Success/skipped arrays mirror the
   * stage path so the UI can render the same summary.
   */
  const bulkFieldUpdate = useMutation({
    mutationFn: async (data: {
      leads: (string | number)[];
      priority?: string;
      leadSourceId?: string | number | null;
      campaignId?: string | number | null;
      /**
       * Lead-level selector custom fields keyed by customInputId (string).
       * Value is the option id, or the literal `"none"` to clear the
       * field. Empty / missing keys are ignored.
       */
      customFields?: Record<string, string>;
    }) => {
      const payload: any = {
        leads: data.leads.map((id) => Number(id)).filter((id) => !isNaN(id)),
      };
      if (data.priority !== undefined && data.priority !== "") {
        payload.priority = data.priority;
      }
      if (data.leadSourceId !== undefined && data.leadSourceId !== "") {
        payload.leadSourceId =
          data.leadSourceId === null ? null : Number(data.leadSourceId);
      }
      if (data.campaignId !== undefined && data.campaignId !== "") {
        payload.campaignId =
          data.campaignId === null ? null : Number(data.campaignId);
      }
      if (data.customFields) {
        const entries = Object.entries(data.customFields)
          .filter(([, v]) => v !== undefined && v !== "")
          .map(([k, v]) => ({
            customInputId: Number(k),
            itemId: v === "none" ? null : Number(v),
          }))
          .filter(
            (e) =>
              Number.isFinite(e.customInputId) &&
              e.customInputId > 0 &&
              (e.itemId === null ||
                (Number.isFinite(e.itemId) && (e.itemId as number) > 0)),
          );
        if (entries.length > 0) payload.customFields = entries;
      }
      const res = await axiosInstance.post(bulkFieldUpdateEndpoint, payload);
      return res.data;
    },
    onSuccess: (data: any) => {
      const success = Number(data?.success?.length ?? 0);
      // Always-sync bulk — pop the shared pill in its done state.
      showCompletedBulkPill(
        { kind: "fields", total: Number(data?.totalRequested ?? success) },
        {
          done: success,
          // Carries the server's own per-row `reason` (e.g. the requirement
          // that blocked the move, or "not in the selected source stage"),
          // so a partial bulk is actionable instead of a bare "8 failed".
          subtitle: bulkOutcomeSubtitle(data),
        },
      );
      // Bulk field update (priority / source / campaign / CF) only changes
      // card-visible fields → board + counts.
      invalidateLeadBoard();
      invalidateRecentActivityFeed(); // `lead_updated` feed rows
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message || "Failed to bulk-update lead fields";
      toast.error(msg);
    },
  });

  /**
   * Bulk archive / restore.
   *
   * Server response shape mirrors `bulkStageUpdate`:
   * - sync (HTTP 200): `{ status, mode: "sync", success, skipped, failed,
   *   totalRequested, archive }`
   * - async (HTTP 202): `{ status, mode: "async", jobId, totalLeads }`
   *
   * Caller polls `useBulkJobStatus(jobId)` for the async branch.
   */
  const bulkArchive = useMutation({
    mutationFn: async (data: {
      leads: (string | number)[];
      archive: boolean;
    }) => {
      const payload = {
        leads: data.leads.map((id) => Number(id)).filter((id) => !isNaN(id)),
        archive: data.archive === true,
      };
      const res = await axiosInstance.post(bulkArchiveLeadsEndpoint, payload);
      return res.data;
    },
    onSuccess: (data: any) => {
      const archiving = (data?.archive ?? true) === true;
      const kind = archiving ? ("archive" as const) : ("restore" as const);

      if (data?.mode === "async") {
        // Progress pill polls the job and refreshes caches on completion
        // (previously this branch only fired a static toast — the user had
        // no live progress for async archive/restore from this hook).
        showBulkToast(data.jobId, {
          kind,
          total: Number(data.totalLeads ?? 0),
        });
        return;
      }
      const success = Number(data?.success?.length ?? 0);
      // Sync branch — same pill surface, popped done.
      showCompletedBulkPill(
        { kind, total: Number(data?.totalRequested ?? success) },
        {
          done: success,
          // Carries the server's own per-row `reason` (e.g. the requirement
          // that blocked the move, or "not in the selected source stage"),
          // so a partial bulk is actionable instead of a bare "8 failed".
          subtitle: bulkOutcomeSubtitle(data),
        },
      );
      invalidateLeadBoard();
      invalidateRecentActivityFeed(); // `lead_archived` / `lead_restored` rows
      // The archive panel is keyed ["archived-leads"] (useArchive), NOT
      // ["leads","archived"] — the old key matched nothing, so an open
      // archive view didn't refresh after a bulk archive/restore.
      queryClient.invalidateQueries({ queryKey: ["archived-leads"] });
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message || "Failed to bulk-archive leads";
      toast.error(msg);
    },
  });

  return {
    createLead,
    updateLead,
    deleteLead,
    updateStage,
    patchLeadSilently,
    assignLeads,
    moveAssignment,
    unassignAssignee,
    peerTransfer,
    branchTransfer,
    bulkStageUpdate,
    bulkFieldUpdate,
    bulkArchive,
  };
}
